// ============================================================================
// GESTIMMONY — Portail Locataire : Accueil, Quittances, Bail, Incidents, Profil
// ============================================================================
import { useEffect, useState, useRef } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import SignaturePad from "@/components/app/SignaturePad";
import { DatePicker } from "@/components/app/DatePicker";
import { toast } from "sonner";
import {
  Receipt, FileText, AlertTriangle, Download, Plus, Home,
  CheckCircle2, Clock, Mail, Phone, PenLine, ShieldCheck, Upload, Trash2, Scale,
} from "lucide-react";

// ---------- Accueil ---------- //
export function PortalHomePage() {
  const { me } = useOutletContext();
  const [lease, setLease] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    api.get("/portal/lease").then(({ data }) => setLease(data));
    api.get("/portal/receipts").then(({ data }) => setLastReceipt(data[0] || null));
    api.get("/portal/incidents").then(({ data }) => setIncidents(data.slice(0, 3)));
  }, []);

  return (
    <div className="space-y-6" data-testid="portal-home">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Bienvenue {me.first_name}</h1>
        <p className="text-sm text-slate-500 mt-1">Voici un résumé de votre location.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="portal-home-lease-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-teal-700">
            <Home className="h-3.5 w-3.5" /> Mon logement
          </div>
          {lease?.property ? (
            <>
              <div className="mt-2 font-heading font-bold text-slate-900">{lease.property.name || lease.property.address}</div>
              <div className="text-xs text-slate-500 mt-0.5">{lease.property.address}, {lease.property.city}</div>
              <div className="mt-3 text-sm">
                <div className="text-slate-500">Loyer + charges</div>
                <div className="font-heading font-bold text-2xl text-slate-900">
                  {fmtEUR((lease.rent_amount || 0) + (lease.charges_amount || 0))}
                </div>
              </div>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-400">Aucun bail actif</div>
          )}
        </Card>

        <Card className="p-5" data-testid="portal-home-receipt-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-teal-700">
            <Receipt className="h-3.5 w-3.5" /> Dernière quittance
          </div>
          {lastReceipt ? (
            <>
              <div className="mt-2 font-heading font-bold text-slate-900">{lastReceipt.period}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {lastReceipt.status === "partiel" ? "Partielle" : "Payée"} : {fmtEUR(lastReceipt.amount_paid ?? lastReceipt.paid_amount ?? 0)}
                {lastReceipt.total_due != null && (lastReceipt.reste_du ?? 0) > 0 && <> / {fmtEUR(lastReceipt.total_due)}</>}
              </div>
              <Link to="/portail/quittances">
                <Button variant="outline" size="sm" className="mt-3">Voir toutes</Button>
              </Link>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-400">Aucune quittance disponible</div>
          )}
        </Card>

        <Card className="p-5" data-testid="portal-home-incident-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-teal-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Vos signalements
          </div>
          <div className="mt-2 font-heading font-bold text-3xl text-slate-900">{incidents.length}</div>
          <div className="text-xs text-slate-500">en cours</div>
          <Link to="/portail/incidents">
            <Button variant="outline" size="sm" className="mt-3">Nouveau signalement</Button>
          </Link>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-teal-700" />
          <h2 className="font-heading font-bold">Votre bailleur</h2>
        </div>
        <div className="text-sm text-slate-700">
          <div className="font-semibold">{me.landlord.name}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
            {me.landlord.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{me.landlord.email}</span>}
            {me.landlord.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{me.landlord.phone}</span>}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------- Quittances ---------- //
export function PortalReceiptsPage() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/portal/receipts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const download = async (rid, period) => {
    try {
      const res = await api.get(`/portal/receipts/${rid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `quittance-${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Quittance téléchargée");
    } catch (e) { toast.error("Erreur téléchargement : " + (e.response?.data?.detail || e.message)); }
  };

  const badgeFor = (status) => {
    const map = {
      paye: { label: "Payée", cls: "bg-emerald-100 text-emerald-800" },
      partiel: { label: "Partielle", cls: "bg-amber-100 text-amber-800" },
      en_attente: { label: "En attente", cls: "bg-slate-100 text-slate-700" },
      retard: { label: "En retard", cls: "bg-rose-100 text-rose-800" },
    };
    const m = map[status] || map.en_attente;
    return <Badge className={`${m.cls} border-0`}>{m.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="portal-receipts">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Mes quittances</h1>
        <p className="text-sm text-slate-500 mt-1">Toutes vos quittances de loyer disponibles au téléchargement.</p>
      </div>
      {items.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">Aucune quittance disponible pour le moment.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`receipt-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-heading font-bold text-slate-900">{r.period}</div>
                {badgeFor(r.status)}
              </div>
              <div className="text-xs text-slate-500">Montant payé</div>
              <div className="font-heading text-xl font-bold text-slate-900">{fmtEUR(r.amount_paid ?? 0)}</div>
              {r.total_due != null && (r.reste_du ?? 0) > 0 && (
                <div className="text-xs text-rose-700 mt-1">Reste dû : <b>{fmtEUR(r.reste_du)}</b> sur {fmtEUR(r.total_due)}</div>
              )}
              {r.paid_at && <div className="text-xs text-slate-500 mt-1">Le {fmtDate(r.paid_at)}</div>}
              <Button
                onClick={() => download(r.id, r.period)}
                data-testid={`receipt-download-${r.id}`}
                variant="outline"
                size="sm"
                className="mt-3 w-full"
              >
                <Download className="h-4 w-4 mr-2" /> Télécharger le PDF
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Bail ---------- //
export function PortalLeasePage() {
  const [lease, setLease] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [, setSigning] = useState(false);
  const load = () => api.get("/portal/lease").then(({ data }) => setLease(data));
  useEffect(() => { load(); }, []);

  const submitSignature = async (dataUrl) => {
    setSigning(true);
    try {
      await api.post("/portal/lease/sign", { signature: dataUrl });
      toast.success("Bail signé ! Votre bailleur en est informé.");
      setSignOpen(false);
      load();
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    } finally {
      setSigning(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/portal/lease/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "bail.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bail téléchargé");
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6" data-testid="portal-lease">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Mon bail</h1>
        <p className="text-sm text-slate-500 mt-1">Consultez les termes de votre contrat et téléchargez la version signée.</p>
      </div>
      {!lease ? (
        <Card className="p-10 text-center text-slate-400">Aucun bail actif</Card>
      ) : (
        <>
          <Card className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</div>
                <div className="mt-1 font-semibold capitalize">{lease.type}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Loyer + charges</div>
                <div className="mt-1 font-semibold">{fmtEUR((lease.rent_amount || 0) + (lease.charges_amount || 0))}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Début</div>
                <div className="mt-1 font-semibold">{fmtDate(lease.start_date)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Fin prévue</div>
                <div className="mt-1 font-semibold">{fmtDate(lease.end_date)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Dépôt de garantie</div>
                <div className="mt-1 font-semibold">{fmtEUR(lease.deposit_amount || 0)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Jour de paiement</div>
                <div className="mt-1 font-semibold">Le {lease.payment_day} du mois</div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm">
              {lease.signed ? (
                <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="text-emerald-700 font-semibold">Bail signé par les deux parties</span></>
              ) : (
                <><Clock className="h-4 w-4 text-amber-600" /> <span className="text-amber-700 font-semibold">Signatures en cours{(() => { const parts = [lease.landlord_signed && "bailleur", lease.tenant_signed && "locataire"].filter(Boolean); return parts.length ? ` (${parts.join(" + ")} déjà signé)` : " (en attente de signature)"; })()}</span></>
              )}
            </div>
          </Card>
          <div className="flex flex-wrap justify-end gap-2">
            {lease.landlord_signed && !lease.tenant_signed && (
              <Button
                data-testid="portal-lease-sign-btn"
                onClick={() => setSignOpen(true)}
                className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white shadow-md"
              >
                <PenLine className="h-4 w-4 mr-2" /> Signer mon bail
              </Button>
            )}
            <Button data-testid="portal-lease-download" onClick={downloadPdf} disabled={downloading} variant={lease.landlord_signed && !lease.tenant_signed ? "outline" : "default"} className={lease.landlord_signed && !lease.tenant_signed ? "" : "bg-[hsl(var(--primary))]"}>
              <Download className="h-4 w-4 mr-2" /> {downloading ? "Téléchargement…" : "Télécharger le bail PDF"}
            </Button>
          </div>
        </>
      )}
      <SignaturePad
        open={signOpen}
        onOpenChange={setSignOpen}
        onSave={submitSignature}
        title="Signer mon bail à distance"
      />
    </div>
  );
}

// ---------- Incidents ---------- //
export function PortalIncidentsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "normale" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/portal/incidents").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/incidents", form);
      toast.success("Signalement envoyé");
      setOpen(false);
      setForm({ title: "", description: "", priority: "normale" });
      load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const statusColors = {
    declare: "bg-slate-100 text-slate-700",
    ouvert: "bg-amber-100 text-amber-800",
    en_cours: "bg-blue-100 text-blue-800",
    resolu: "bg-emerald-100 text-emerald-800",
    ferme: "bg-slate-100 text-slate-700",
  };
  const statusLabels = {
    declare: "Déclaré",
    ouvert: "Ouvert",
    en_cours: "En cours",
    resolu: "Résolu",
    ferme: "Fermé",
  };

  return (
    <div className="space-y-6" data-testid="portal-incidents">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Signaler un problème</h1>
          <p className="text-sm text-slate-500 mt-1">Dégât, panne, dysfonctionnement — votre bailleur est notifié immédiatement.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="portal-incident-new" className="bg-[hsl(var(--primary))]">
              <Plus className="h-4 w-4 mr-2" /> Nouveau signalement
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="portal-incident-dialog">
            <DialogHeader>
              <DialogTitle>Signaler un problème</DialogTitle>
              <DialogDescription>Votre bailleur reçoit ce signalement immédiatement.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Objet</Label>
                <Input data-testid="portal-incident-title" required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Fuite d'eau salle de bain" />
              </div>
              <div>
                <Label>Urgence</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="portal-incident-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basse">Basse</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description détaillée</Label>
                <Textarea data-testid="portal-incident-desc" required minLength={5} rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez précisément le problème (localisation, fréquence, urgence…)" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button data-testid="portal-incident-submit" type="submit" disabled={busy} className="bg-[hsl(var(--primary))]">
                  {busy ? "Envoi…" : "Envoyer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">Aucun signalement pour le moment.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((inc) => (
            <Card key={inc.id} className="p-4" data-testid={`incident-${inc.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-bold text-slate-900">{inc.title}</h3>
                    <Badge className={`${statusColors[inc.status] || statusColors.declare} border-0`}>{statusLabels[inc.status] || statusLabels.declare}</Badge>
                    <Badge variant="outline" className="text-xs capitalize">{inc.priority}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{inc.description}</p>
                  <div className="text-xs text-slate-400 mt-1">
                    {inc.reported_by === "tenant" ? "Signalé par vous" : "Signalé par le bailleur"} · {fmtDate(inc.created_at)}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Profil ---------- //
export function PortalProfilePage() {
  const { me } = useOutletContext();
  const [insurance, setInsurance] = useState(null);
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/portal/insurance").then(({ data }) => {
      setInsurance(data);
      if (data?.expiry_date) setExpiry(data.expiry_date.slice(0, 10));
    }).catch(() => {});
  }, []);

  const uploadInsurance = async (files) => {
    const f = files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Fichier trop lourd (>10 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (expiry) fd.append("expiry_date", expiry);
      const { data } = await api.post("/portal/insurance", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInsurance(data);
      toast.success("Attestation d'assurance déposée");
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadInsurance = async () => {
    try {
      const res = await api.get("/portal/insurance/file", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener";
      a.download = insurance?.filename || "assurance";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const removeInsurance = async () => {
    try { await api.delete("/portal/insurance"); setInsurance(null); setExpiry(""); toast.success("Attestation retirée"); }
    catch { toast.error("Suppression impossible"); }
  };

  const saveExpiry = async () => {
    try {
      const { data } = await api.patch("/portal/insurance/expiry", { expiry_date: expiry || null });
      setInsurance((ins) => ins ? { ...ins, expiry_date: data.expiry_date } : ins);
      toast.success("Date d'échéance enregistrée");
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-profile">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Mon profil</h1>
        <p className="text-sm text-slate-500 mt-1">Informations transmises par votre bailleur.</p>
      </div>
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField label="Prénom" value={me.first_name} />
          <ProfileField label="Nom" value={me.last_name} />
          <ProfileField label="Email" value={me.email} />
          <ProfileField label="Téléphone" value={me.phone} />
        </div>
      </Card>

      <Card className="p-6" data-testid="portal-insurance-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-teal-700 mb-3">
          <ShieldCheck className="h-4 w-4" /> Assurance habitation
        </div>
        {insurance ? (
          <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="portal-insurance-current">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-slate-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{insurance.filename}</div>
                <div className="text-xs text-slate-500">
                  {insurance.expiry_date ? `Échéance : ${fmtDate(insurance.expiry_date)}` : "Sans date d'échéance"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button data-testid="portal-insurance-download" variant="ghost" size="icon" onClick={downloadInsurance}><Download className="h-4 w-4" /></Button>
              <Button data-testid="portal-insurance-delete" variant="ghost" size="icon" onClick={removeInsurance}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Déposez votre attestation d'assurance habitation (obligatoire). Votre bailleur pourra la consulter.</p>
        )}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <Label className="text-xs">Date d'échéance</Label>
            <div className="flex items-center gap-2 mt-1">
              <DatePicker data-testid="portal-insurance-expiry" value={expiry} onChange={setExpiry} placeholder="Date d'échéance" className="w-[200px]" />
              {insurance && (
                <Button data-testid="portal-insurance-save-expiry" type="button" variant="outline" size="sm" onClick={saveExpiry}>Enregistrer</Button>
              )}
            </div>
          </div>
          <label className="inline-flex items-center gap-1 text-sm bg-teal-50 border border-teal-200 hover:bg-teal-100 rounded px-4 py-2 cursor-pointer" data-testid="portal-insurance-upload">
            <Upload className="h-4 w-4" /> {uploading ? "Envoi…" : (insurance ? "Remplacer l'attestation" : "Déposer l'attestation")}
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadInsurance(e.target.files)} />
          </label>
        </div>
      </Card>

      <Card className="p-6 bg-teal-50/30 border-teal-200">
        <div className="text-xs uppercase tracking-wider font-semibold text-teal-700 mb-2">Bailleur</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField label="Nom" value={me.landlord.name} />
          <ProfileField label="Email" value={me.landlord.email} />
          <ProfileField label="Téléphone" value={me.landlord.phone} />
        </div>
      </Card>
      <p className="text-xs text-slate-400 italic">
        Une information est incorrecte ? Contactez votre bailleur pour toute mise à jour.
      </p>
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900">{value || "—"}</div>
    </div>
  );
}



// ---------- Mes courriers ---------- //
export function PortalLettersPage() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/letters")
      .then(({ data }) => setLetters(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const download = async (l) => {
    try {
      const res = await api.get(`/portal/letters/${l.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = l.filename || "courrier.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="space-y-6" data-testid="portal-letters">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Mes courriers</h1>
        <p className="text-sm text-slate-500 mt-1">Les courriers que votre bailleur vous a adressés.</p>
      </div>
      {loading ? (
        <div className="text-slate-400 text-sm">Chargement…</div>
      ) : letters.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 text-sm" data-testid="portal-letters-empty">
          Aucun courrier pour le moment.
        </Card>
      ) : (
        <div className="space-y-3">
          {letters.map((l) => (
            <Card key={l.id} data-testid={`portal-letter-${l.id}`} className="p-4 flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-slate-900 truncate">{l.title}</div>
                <div className="text-xs text-slate-500">{fmtDate(l.created_at)}</div>
              </div>
              <Button data-testid={`portal-letter-dl-${l.id}`} onClick={() => download(l)} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" /> Télécharger
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Régularisations des charges ---------- //
export function PortalRegularisationsPage() {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/regularisations")
      .then(({ data }) => setRegs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const download = async (r) => {
    try {
      const res = await api.get(`/portal/regularisations/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `regularisation-${r.year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="space-y-6" data-testid="portal-regularisations">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Régularisations des charges</h1>
        <p className="text-sm text-slate-500 mt-1">
          Le décompte annuel entre les provisions versées et les charges récupérables réelles.
        </p>
      </div>
      {loading ? (
        <div className="text-slate-400 text-sm">Chargement…</div>
      ) : regs.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 text-sm" data-testid="portal-regularisations-empty">
          Aucun décompte de régularisation pour le moment.
        </Card>
      ) : (
        <div className="space-y-3">
          {regs.map((r) => {
            const credit = (r.balance ?? 0) >= 0;
            return (
              <Card key={r.id} data-testid={`portal-reg-${r.id}`} className="p-5">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
                    <Scale className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-slate-900">Régularisation {r.year}</div>
                    <div className="text-xs text-slate-500">{fmtDate(r.created_at)}</div>
                  </div>
                  <Button data-testid={`portal-reg-dl-${r.id}`} onClick={() => download(r)} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" /> PDF
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-stone-100">
                  <div>
                    <div className="text-xs text-slate-500">Provisions versées</div>
                    <div className="font-heading font-semibold text-slate-900">{fmtEUR(r.provisions_total)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Charges récupérables réelles</div>
                    <div className="font-heading font-semibold text-slate-900">{fmtEUR(r.real_recuperable)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">{credit ? "Trop-perçu à vous restituer" : "Complément dû"}</div>
                    <div className={`font-heading font-bold ${credit ? "text-teal-700" : "text-rose-600"}`} data-testid={`portal-reg-balance-${r.id}`}>
                      {fmtEUR(Math.abs(r.balance ?? 0))}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}